import { useEffect, useRef } from "react";

import { isPageHidden } from "@helpers/hooks/use-page-visible";

// Calendar-insight summaries are generated in the background after onboarding
// (one async LLM task per direct report, a few seconds each). The homepage
// usually loads before they land, so poll a few times to pull them in without a
// manual reload.
export const CALENDAR_INSIGHT_POLL_INTERVAL_MS = 4000;
export const CALENDAR_INSIGHT_MAX_POLL_ATTEMPTS = 5;

/**
 * Refetches the homepage metadata query a few times so background-generated
 * calendar insights appear without a manual reload.
 *
 * Stops as soon as at least one insight has landed and the populated count is no
 * longer growing (stragglers have caught up), or after a hard cap of attempts.
 * Polling pauses while the tab is hidden so a backgrounded tab doesn't burn
 * attempts.
 */
export const usePollForCalendarInsights = ({
  populatedInsightCount,
  enabled,
  refetch,
}: {
  populatedInsightCount: number;
  enabled: boolean;
  refetch: () => void;
}) => {
  // Refs so the interval always reads the latest values without restarting.
  const latestCountRef = useRef(populatedInsightCount);
  latestCountRef.current = populatedInsightCount;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!enabled) return undefined;

    let attempts = 0;
    let previousCount = latestCountRef.current;

    const intervalId = window.setInterval(() => {
      // Don't burn an attempt while the tab is hidden; resume when visible.
      if (isPageHidden()) return;

      const currentCount = latestCountRef.current;
      const hasStabilized = currentCount > 0 && currentCount === previousCount;
      if (hasStabilized || attempts >= CALENDAR_INSIGHT_MAX_POLL_ATTEMPTS) {
        window.clearInterval(intervalId);
        return;
      }

      previousCount = currentCount;
      attempts += 1;
      refetchRef.current();
    }, CALENDAR_INSIGHT_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [enabled]);
};
